import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchLookupEntry,
  getCachedLookupMap,
} from "../api/mutationRequests.api";
import type {
  ChildSummary,
  EntityLookupKind,
  FamilySummary,
  LookupEntry,
  LookupMap,
  ParentSummary,
  UserSummary,
} from "../types/mutationRequests.types";
import { normalizeEntityIds } from "../utils/mutationRequests.utils";

type LookupState = {
  userLookup: LookupMap<UserSummary>;
  parentLookup: LookupMap<ParentSummary>;
  childLookup: LookupMap<ChildSummary>;
  familyLookup: LookupMap<FamilySummary>;
  loading: boolean;
};

const lookupForKind = <TEntry extends LookupEntry>(kind: EntityLookupKind): LookupMap<TEntry> =>
  getCachedLookupMap<TEntry>(kind);

export const useMutationEntityLookups = () => {
  const mountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const ensureLookup = useCallback(async (kind: EntityLookupKind, identifiers: readonly unknown[] | unknown) => {
    const ids = normalizeEntityIds(identifiers);
    if (!ids.length) return;

    const missing = ids.filter((id) => !getCachedLookupMap(kind).has(id));
    if (!missing.length) return;

    if (mountedRef.current) setLoading(true);
    try {
      await Promise.all(missing.map((id) => fetchLookupEntry(kind, id)));
      if (mountedRef.current) setVersion((current) => current + 1);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  return {
    userLookup: lookupForKind<UserSummary>("user"),
    parentLookup: lookupForKind<ParentSummary>("parent"),
    childLookup: lookupForKind<ChildSummary>("child"),
    familyLookup: lookupForKind<FamilySummary>("family"),
    loading,
    version,
    ensureLookup,
  } satisfies LookupState & {
    version: number;
    ensureLookup: (kind: EntityLookupKind, identifiers: readonly unknown[] | unknown) => Promise<void>;
  };
};
